import { App, Modal, Select, Typography, Badge } from "antd";
import React, { useState, useEffect, useCallback } from "react";
import { makeRequest } from "../../helpers/axios-manipulator";

const { Text } = Typography;

type Props = {
    open: boolean;
    billingId: number;
    onClose: () => void;
    onSuccess: () => void;
};

type UserOption = {
    id: string;
    email: string;
    display_name: string;
    google_id: string | null;
    in_use?: boolean;
};

type BillingUser = {
    email: string;
    google_id: string | null;
};

const AddBillingUserModal = ({ open, billingId, onClose, onSuccess }: Props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [linkedEmails, setLinkedEmails] = useState<Set<string>>(new Set());
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
    const { message } = App.useApp();

    // Fetch all billing users to check which are already linked
    const fetchLinkedUsers = useCallback(async () => {
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: '/api-billing-users-all',
                headers: {},
            });
            const emails = new Set<string>(
                (response || [])
                    .map((bu: BillingUser) => bu.email?.toLowerCase())
                    .filter(Boolean)
            );
            setLinkedEmails(emails);
        } catch (error) {
            console.error('Error fetching linked users:', error);
            setLinkedEmails(new Set());
        }
    }, []);

    // Fetch users for the dropdown
    const fetchUsers = useCallback(async (search: string = '') => {
        setSearchLoading(true);
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api-users?type=1,2,3,4`,
                headers: {},
            });
            // Filter by search term if provided
            let filtered = response || [];
            if (search) {
                const searchLower = search.toLowerCase();
                filtered = filtered.filter((user: UserOption) =>
                    (user.email && user.email.toLowerCase().includes(searchLower)) ||
                    (user.display_name && user.display_name.toLowerCase().includes(searchLower))
                );
            }
            // Mark users that are already linked
            filtered = filtered.map((user: UserOption) => ({
                ...user,
                in_use: user.email ? linkedEmails.has(user.email.toLowerCase()) : false,
            }));
            setUsers(filtered);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setSearchLoading(false);
        }
    }, [linkedEmails]);

    // Fetch linked users first, then users when modal opens
    useEffect(() => {
        if (open) {
            fetchLinkedUsers();
            setSelectedUserId(undefined);
        }
    }, [open, fetchLinkedUsers]);

    // Fetch users after linked emails are loaded
    useEffect(() => {
        if (open && linkedEmails) {
            fetchUsers();
        }
    }, [open, linkedEmails, fetchUsers]);

    const handleAddUser = async () => {
        if (!selectedUserId) {
            message.error('Please select a user');
            return;
        }

        setLoading(true);
        try {
            await makeRequest({
                origin: window.location.origin,
                method: 'post',
                url: `/api/billing/${billingId}/users`,
                headers: { 'Content-Type': 'application/json' },
                data: { user_id: selectedUserId },
            });

            const selectedUser = users.find(u => u.id === selectedUserId);
            if (selectedUser?.google_id) {
                message.success('User linked successfully!');
            } else {
                message.success('User linked with pending status. Google ID will be captured on first login.');
            }

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Error adding user to billing:', error);
            const errorMsg = error?.response?.data?.error || 'Failed to add user to contract';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            centered
            closable={true}
            maskClosable={false}
            title="Add User to Contract"
            open={open}
            okText="Add User"
            onOk={handleAddUser}
            onCancel={onClose}
            width={500}
            okButtonProps={{ disabled: !selectedUserId, loading }}
            destroyOnHidden={true}
        >
            <div style={{ marginBottom: 8 }}>
                <Text>Select a user to link to this contract:</Text>
            </div>
            <Select
                showSearch
                placeholder="Search by name or email"
                style={{ width: '100%' }}
                value={selectedUserId}
                onChange={(value) => setSelectedUserId(value)}
                loading={searchLoading}
                filterOption={false}
                onSearch={fetchUsers}
                notFoundContent={searchLoading ? 'Loading...' : 'No users found'}
                options={users.map((user) => ({
                    value: user.id,
                    disabled: user.in_use,
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: user.in_use ? 0.5 : 1 }}>
                            <div>
                                <div style={{ fontWeight: 500 }}>
                                    {user.display_name || 'No name'}
                                </div>
                                <div style={{ fontSize: 12, color: '#888' }}>
                                    {user.email || 'No email'}
                                </div>
                            </div>
                            <Badge
                                status={user.in_use ? 'default' : (user.google_id ? 'success' : 'warning')}
                                text={user.in_use ? 'In Use' : (user.google_id ? 'Ready' : 'Pending')}
                                style={{ fontSize: 11 }}
                            />
                        </div>
                    ),
                }))}
            />
            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <strong>Ready:</strong> User has logged in with Google and can access immediately.
                    <br />
                    <strong>Pending:</strong> User has not logged in yet. Access will be granted on first Google login.
                    <br />
                    <strong>In Use:</strong> User is already linked to a contract and cannot be added again.
                </Text>
            </div>
        </Modal>
    );
};

export default AddBillingUserModal;
